import type { ReactElement } from 'react';
import { formatRange, type AvailabilitySegment } from './AvailabilityTimeline.js';
import { DefinitionList, type Definition } from '../data/index.js';
import { formatInterval } from '../../lib/format/interval.js';
import type { AvailabilityConstraints } from '../../lib/api/queries/equipment.js';

export interface AvailabilitySummaryProps {
  segments: AvailabilitySegment[];
  constraints: AvailabilityConstraints | null;
}

/**
 * The intervals as text, and the booking constraints beside them.
 *
 * This is the authoritative rendering of P-Q013 — the timeline bar is the
 * decorative companion to it, never the reverse. Two rules hold here:
 *
 *   · A blocked window renders as a DATE RANGE AND NOTHING ELSE. The API sends
 *     `reason: null` to every non-owner (§9.5), and this component has no prop
 *     through which a reason could be passed, so it cannot leak one even by
 *     mistake.
 *   · Colour never carries the verdict alone — every row is labelled in words.
 */
export function AvailabilitySummary({
  segments,
  constraints,
}: AvailabilitySummaryProps): ReactElement {
  const constraintItems: Definition[] = [];
  const push = (term: string, value: string | null): void => {
    if (value !== null) constraintItems.push({ term, value });
  };
  if (constraints !== null) {
    push('Minimum rental', formatInterval(constraints.minimum_rental));
    push('Minimum notice', formatInterval(constraints.minimum_notice));
    push('Booking horizon', formatInterval(constraints.maximum_horizon));
    push('Outbound buffer', formatInterval(constraints.buffers.outbound));
    push('Inbound buffer', formatInterval(constraints.buffers.inbound));
    push('Inspection buffer', formatInterval(constraints.buffers.inspection));
  }

  return (
    <>
      <ul className="availability__list">
        {segments.map((segment) => (
          <li
            key={`${segment.start}-${segment.free ? 'free' : 'blocked'}`}
            className="availability__row"
          >
            <span
              className={`availability__marker availability__marker--${
                segment.free ? 'free' : 'blocked'
              }`}
              aria-hidden="true"
            />
            <span className="availability__range numeric">
              {formatRange(segment.start, segment.end)}
            </span>
            <span className="availability__verdict">
              {segment.free ? 'Available' : 'Unavailable'}
            </span>
          </li>
        ))}
      </ul>

      {constraintItems.length === 0 ? null : (
        <>
          <h3 className="subsection-title">Booking constraints</h3>
          <DefinitionList items={constraintItems} label="Booking constraints" />
        </>
      )}
    </>
  );
}
